import type { Component, ComponentProps } from "solid-js";
import {
  type ConnectivityContext,
  type ConnectivityStateName,
  isConnectivityStateName,
} from "@space/shared";
import {
  CheckCircle2,
  CloudUpload,
  Hammer,
  RefreshCw,
  RotateCw,
  User,
  WifiOff,
} from "lucide-solid";

/** Declarative UI properties for each connectivity state. */
interface ConnectivityUIMeta {
  /** Lucide icon component */
  icon: Component<ComponentProps<"svg">>;
  /** Design token key for the icon/text color */
  colorToken: "muted" | "success" | "accent" | "warning" | "danger" | "default";
  /** Tailwind text class for the pill label */
  textClass: string;
  /** Tailwind bg class for the pill background */
  textBgClass?: string;
  bgClass: string;
  /** Whether the icon should spin */
  spin: boolean;
  /** I18n label key (matched in the component) */
  labelKey: ConnectivityLabelKey;
}

/** Stable, machine-derived label keys for each connectivity pill state. */
export type ConnectivityLabelKey =
  | "offline"
  | "connected"
  | "checking"
  | "reconnecting"
  | "maintenance"
  | "unauthenticated";

/** Default UI config keyed by state name, for easy lookup. */
const connectivityUI: Record<ConnectivityStateName, ConnectivityUIMeta> = {
  initializing: {
    icon: RefreshCw,
    colorToken: "muted",
    textClass: "text-muted-foreground",
    bgClass: "bg-muted/10",
    spin: true,
    labelKey: "checking",
  },
  offline: {
    icon: WifiOff,
    colorToken: "danger",
    textClass: "text-danger",
    bgClass: "bg-danger/20",
    spin: false,
    labelKey: "offline",
  },
  unauthenticated: {
    icon: User,
    colorToken: "default",
    textClass: "text-foreground/50",
    bgClass: "bg-foreground/10",
    spin: false,
    labelKey: "unauthenticated",
  },
  checking: {
    icon: RefreshCw,
    colorToken: "accent",
    textClass: "text-primary",
    bgClass: "bg-primary/20",
    spin: true,
    labelKey: "checking",
  },
  connected: {
    icon: CheckCircle2,
    colorToken: "success",
    textClass: "text-success",
    bgClass: "bg-success/20",
    spin: false,
    labelKey: "connected",
  },
  reconnecting: {
    icon: RotateCw,
    colorToken: "warning",
    textClass: "text-warning",
    bgClass: "bg-warning/20",
    spin: true,
    labelKey: "reconnecting",
  },
  maintenance: {
    icon: Hammer,
    colorToken: "warning",
    textClass: "text-warning",
    bgClass: "bg-warning/20",
    spin: false,
    labelKey: "maintenance",
  },
  manualChecking: {
    icon: RefreshCw,
    colorToken: "accent",
    textClass: "text-primary",
    bgClass: "bg-primary/20",
    spin: true,
    labelKey: "checking",
  },
};

/** Helper to get UI metadata based on the current state and context */
export function getConnectivityUI(
  stateName: string | object,
  context: ConnectivityContext,
): ConnectivityUIMeta {
  const name = (typeof stateName === "string" ? stateName : Object.keys(stateName)[0]) ?? "";
  const base = isConnectivityStateName(name) ? connectivityUI[name] : connectivityUI.offline;

  // Enhance "connected" state with PowerSync status
  if (name === "connected") {
    if (context.isUploading) {
      return {
        ...base,
        icon: RefreshCw,
        colorToken: "accent",
        textClass: "text-primary",
        bgClass: "bg-primary/20",
        spin: true,
        labelKey: "connected",
      };
    }
    if (context.uploadQueueSize > 0) {
      return {
        ...base,
        icon: CloudUpload,
        colorToken: "warning",
        textClass: "text-warning",
        bgClass: "bg-warning/20",
        spin: false,
        labelKey: "connected",
      };
    }
  }

  return base;
}
